import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Standard shadcn-style class merger: prefer later Tailwind classes
 * over earlier ones, drop falsy values.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format an AUD-string-or-number as a display price.
 * Backend serialises Decimals as strings to preserve precision; we
 * keep two decimals on display.
 */
export function formatAUD(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a signed AUD value with a leading + or - sign for the
 * breakdown panel ("-$50.00", "+$9.95").
 */
export function formatAUDSigned(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(n)) return "—";
  if (n === 0) return "$0.00";
  const formatted = formatAUD(Math.abs(n));
  return n < 0 ? `−${formatted}` : `+${formatted}`;
}

/**
 * Title-case a display label so EVERY word starts with a capital
 * ("women's formal dress shoes" -> "Women's Formal Dress Shoes").
 *
 * Browse mixes two kinds of title in one list:
 *   1. Broad category labels — the de-slugified search query, all-lowercase
 *      ("samba dance shoes"). These are what we want to capitalise.
 *   2. Deep product titles — already properly cased with INTENTIONAL mid-word
 *      capitals ("Apple AirPods Pro 3", "Sony WH-1000XM5", "Bose QuietComfort
 *      Ultra Headphones", "iPhone"). These must not be mangled.
 *
 * So a word is only touched when it is all-lowercase (contains no capital):
 * its first character is capitalised. Any word that already holds a capital is
 * left exactly as-is, which preserves "AirPods" / "WH-1000XM5" / "iPhone".
 * Whitespace and punctuation are preserved. A digit-led word ("3", "42mm") is
 * unchanged because its first character has no uppercase form.
 */
export function titleCase(s: string): string {
  return (s ?? "").replace(/\S+/g, (word) =>
    /[A-Z]/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
  );
}

/**
 * Format a timestamp in Australian Eastern time, regardless of where
 * the server or the user's browser is. Mio is an AU-only product;
 * "Updated 12 May 2026, 7:38 pm" must mean Sydney time, not UTC.
 *
 * Use this helper for every user-facing timestamp on the site. The
 * default `Date.toLocaleString` without an explicit timeZone defaults
 * to the runtime's local zone (server SSR = UTC on DO App Platform;
 * browser SSR = the user's machine zone — neither is what we want).
 */
export function formatAUTime(
  value: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-AU", {
    timeZone: "Australia/Sydney",
    dateStyle: "medium",
    timeStyle: "short",
    ...options,
  });
}
